import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

interface InputProps {
	label?: string;
	placeholder?: string;
	id?: string;
	comment?: string;
	width?: string;
	inputClass?: string;
	value?: string;
	onChange?: (val: string, e?: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onFocus?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onEnter?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
	onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
	autoFocus?: boolean;
	showClearIcon?: boolean;
	onClickClearIcon?: () => void;
	showSearchIcon?: boolean;
	onClickSearchIcon?: () => void;
	disabled?: boolean;
	forwardRef?: React.RefObject<HTMLInputElement>;
	className?: string;
	type?: 'text' | 'number';
	inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

export default function Input(props: InputProps) {
	const backupRef = useRef<HTMLInputElement>(null);
	const inputRef = props.forwardRef ? props.forwardRef : backupRef;
	const { type = 'text', inputProps } = props;

	useEffect(() => {
		if (props.autoFocus) {
			setTimeout(() => {
				inputRef.current?.focus();
			}, 250);
		}
	}, [props.autoFocus, inputRef]);

	const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		props.onChange?.(event.target.value, event);
	};

	const onBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
		props.onBlur?.(event);
	};

	const onFocus = (event: React.ChangeEvent<HTMLInputElement>) => {
		props.onFocus?.(event);
	};

	const onKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
		props.onKeyUp?.(event);
		if (event.key === 'Enter') {
			props.onEnter?.(event);
		}
	};

	const id = props.id ? props.id : uuid();
	const placeholder = props.placeholder === undefined ? '' : props.placeholder;
	const style: React.CSSProperties = {};
	const disabled = props.disabled === true ? true : false;

	if (props.width) {
		style.width = props.width;
	}

	return (
		<div className={`${props.className ?? ''} form-group`}>
			{props.label && <label htmlFor={id}>{props.label}</label>}
			<InputGroup
				value={props.value ?? ''}
				showClearIcon={props.showClearIcon ?? false}
				showSearchIcon={props.showSearchIcon ?? false}
			>
				<input
					ref={inputRef}
					onBlur={onBlur}
					onKeyUp={onKeyUp}
					onChange={onChange}
					onFocus={onFocus}
					value={props.value}
					type={type}
					className={`${props.inputClass ? props.inputClass : ''} form-control`}
					id={id}
					placeholder={placeholder}
					style={style}
					disabled={disabled}
					{...inputProps}
				/>
				{props.showSearchIcon && (
					<SearchButtonContainer onClick={props.onClickSearchIcon}>
						<i className="fas fa-search" />
					</SearchButtonContainer>
				)}
				<i
					className="fas fa-times"
					onClick={() => {
						props.onChange?.('');
						props.onClickClearIcon?.();
					}}
				/>
			</InputGroup>
			{props.comment && (
				<small id={`${id}Help`} className="form-text text-muted">
					{props.comment}
				</small>
			)}
		</div>
	);
}

const SearchButtonContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 38px;
	height: 38px;
	background: #ececec;

	&:hover {
		background: #e2e2e2;
		cursor: pointer;

		.fa-search {
			color: #000000;
		}
	}
`;

const InputGroup = styled.div<{ value: string; showClearIcon: boolean; showSearchIcon: boolean }>`
	position: relative;
	display: flex;

	.fa-times {
		position: absolute;
		right: ${(props) => (props.showSearchIcon ? '50px' : '12px')};
		top: 11px;
		display: ${(props) => (props.showClearIcon && props.value ? 'block' : 'none')};

		&:hover {
			cursor: pointer;
			color: #00000040;
		}
	}

	input {
		display: block;
		width: 100%;
		height: 34px;
		padding: 6px 12px;
		font-size: 14px;
		line-height: 1.42857143;
		color: #555;
		background-color: #fff;
		background-image: none;
		border: 1px solid #ccc;
		border-radius: 0px;
		box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
		transition:
			border-color ease-in-out 0.15s,
			box-shadow ease-in-out 0.15s;
	}
	input:focus {
		border-color: #66afe9;
		outline: 0;
		box-shadow:
			inset 0 1px 1px rgb(0 0 0 / 8%),
			0 0 8px rgb(102 175 233 / 60%);
	}
`;
